import type { LessonDeliveryStatus } from '@classroom-ai/types';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { agentTrpc } from 'src/lib/agent-trpc';

import { BOARD_QUERY_KEY } from '../board/use-lesson-plans-board';

// ----------------------------------------------------------------------

export const DELIVERY_QUERY_KEY = 'lessonDelivery';

/**
 * A class's "Mark taught" marks (epic #647) with what the caller may do: `canMark` and
 * `canReadNotes` come from the server, which applies the same rules as the write.
 * `onChanged` refreshes whatever else reads the mark (the board).
 */
export function useLessonDelivery(classId: string | null, onChanged?: () => unknown) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: [DELIVERY_QUERY_KEY, classId],
    enabled: !!classId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    queryFn: () => agentTrpc.lessonDelivery.listForClass({ classId: classId! }),
  });

  const after = async () => {
    // Marks, the Wrap up list, and every board read (week grid cells, calendar) show the mark.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [DELIVERY_QUERY_KEY] }),
      queryClient.invalidateQueries({ queryKey: [BOARD_QUERY_KEY] }),
    ]);
    await onChanged?.();
  };

  return {
    data: query.data,
    loading: query.isPending && !!classId,
    mark: async (input: {
      lessonPlanId: string;
      status: LessonDeliveryStatus;
      taughtOn?: string | null;
      notes?: string | null;
      tags?: string[];
      carryOver?: boolean;
    }) => {
      if (!classId) return;
      await agentTrpc.lessonDelivery.mark({ classId, ...input });
      await after();
    },
    clear: async (lessonPlanId: string) => {
      if (!classId) return;
      await agentTrpc.lessonDelivery.clear({ classId, lessonPlanId });
      await after();
    },
  };
}
